import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import { IconTeams } from '../../../icons/IconTeams'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { NotificationTypeChip } from './NotificationTypeChip'
import { createNotificationNavHandlers, getActorPathKey, getTeamSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { NotificationDecisionStatus } from './NotificationDecisionStatus'
import { notificationBodyWrapSx, notificationMessageClampSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'team.invitation.decided' }>
}

function teamTitleFromBlock(team: { title?: string; name?: string } | null | undefined): string {
  const title = typeof team?.title === 'string' ? team.title.trim() : ''
  if (title.length > 0) return title
  const name = typeof team?.name === 'string' ? team.name.trim() : ''
  return name.length > 0 ? name : 'команда'
}

export function TeamCatalogInvitationDecidedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onTeamClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload
  const team = payload?.team ?? {}
  const actor = payload?.actor ?? {}
  const invitation = payload?.invitation ?? {}
  const teamTitle = teamTitleFromBlock(team)
  const actorName = typeof actor.fullname === 'string' ? actor.fullname : 'Пользователь'
  const status = typeof invitation.status === 'string' ? invitation.status : 'unknown'
  const teamAvatarSrc = mediaSourceUrl(team.image_url) ?? undefined
  const teamSlug = getTeamSlug(team)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)
  const responseMessage =
    typeof invitation.response_message === 'string' ? invitation.response_message.trim() : ''

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onTeamClick(e, teamSlug)}
          sx={{ cursor: teamSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={teamTitle}
            avatarSrc={teamAvatarSrc}
            fallbackIcon={<IconTeams fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={
              <>
                <Typography variant="black-12-light" component="span" sx={{ minWidth: 0, ...notificationBodyWrapSx }}>
                  Ответ на приглашение
                </Typography>
                <NotificationTypeChip variant="invitation_decided" />
              </>
            }
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
            sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
          >
            <Avatar
              src={mediaSourceUrl(actor.avatar_url) ?? undefined}
              alt={actorName}
              sx={{ width: 34, height: 34 }}
            />
          </Box>
          <Stack minWidth={0} flex={1} gap={0.25}>
            <Stack direction="row" gap={0.5} alignItems="baseline" minWidth={0} flexWrap="wrap">
              <Typography variant="black-14-normal" component="span" sx={notificationBodyWrapSx}>
                Приглашение в <ProjectNameQuoted name={teamTitle} pathKey={teamSlug} onNameClick={onTeamClick} />
              </Typography>
              <NotificationDecisionStatus status={status} />
            </Stack>
            {responseMessage ? (
              <Typography variant="black-12-light" sx={notificationMessageClampSx}>
                {responseMessage}
              </Typography>
            ) : (
              <Typography variant="black-12-light">
                Ответ от{' '}
                <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                  {actorName}
                </ActorNameSpan>
              </Typography>
            )}
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
